import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Briefcase, MapPin, Buildings } from '@phosphor-icons/react';
import { CompanyLogo } from '../../../../../components/CompanyLogo';
import { CompanyName } from '../../../../../components/CompanyName';
import type { SimilarJob } from '../../../../../types/insights';
import { formatSalaryMinMaxPair } from '../../../../../utils/data/format-salary-min-max-pair';
import { trackJobClick } from '../../../../../utils/analytics/analytics';
import { adjustBrandColor, brandAccentOnDark } from '../../../../../utils/brand-color/adjust-brand-color';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { getFirstConcreteLocationSegment } from '../utils/job-detail-location-parts';
import { Msg, useFormatMessage } from '../../../../../i18n-review/client/Msg';
import { useDocumentDarkClass } from '../../../../../hooks/useDocumentDarkClass';

interface Props {
  similarJobs: SimilarJob[];
  seeAllHref?: string;
  /** Company's brand panel colour (editorial-rail redesign; same value passed as `brandColor` to
   * EditorialHeadlineFacts/EditorialCompanyExtras). When absent, keeps the generic jc-accent green. */
  brandColor?: string;
}

function formatSalary(job: SimilarJob): string | null {
  const { salaryRangeMin, salaryRangeMax, currency = 'EUR', salaryPeriod } = job;
  if (!salaryRangeMin && !salaryRangeMax) return null;

  const symbol = currency === 'EUR' ? '€' : currency;
  const periodLabel = salaryPeriod === 'annual' ? '/yr' : '/mo';

  if (salaryRangeMin && salaryRangeMax) {
    const body = formatSalaryMinMaxPair(
      salaryRangeMin,
      salaryRangeMax,
      (n) => `${symbol}${n.toLocaleString()}`,
      '–',
    );
    return `${body}${periodLabel}`;
  }
  const value = salaryRangeMin ?? salaryRangeMax ?? 0;
  return `${symbol}${value.toLocaleString()}${periodLabel}`;
}

function formatLocation(location: string | string[] | undefined): string {
  if (!location) return '';
  const primary = getFirstConcreteLocationSegment(location);
  return primary.split(',')[0] ?? '';
}

/** One card, styled to match the SRP job card (hit-card-v2.css); brand panel, logo tile,
 * salary chip, meta pills, CTA; minus the interactive-only affordances (match donut,
 * save/share/menu) that don't apply to this read-only list. */
const SimilarJobCard: React.FC<{ job: SimilarJob; onNavigate: () => void }> = ({ job, onNavigate }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const salary = formatSalary(job);
  const city = formatLocation(job.location);
  const title = fixMojibake(job.normalisedTitle || job.title || '');
  const companyKey = job.companyId || '';
  const companyDisplay = job.companyName || companyKey;
  const adjustedBrand = adjustBrandColor(job.brandColour);

  const metaFields: { key: string; label: string }[] = [];
  if (job.remoteness?.trim()) metaFields.push({ key: 'remote', label: fixMojibake(job.remoteness) });
  if (job.jobType?.trim()) metaFields.push({ key: 'jobType', label: fixMojibake(job.jobType) });
  if (job.normalisedRoleLevel?.trim()) metaFields.push({ key: 'roleLevel', label: fixMojibake(job.normalisedRoleLevel) });
  if (job.requiredLanguages && job.requiredLanguages.length > 0) {
    metaFields.push({ key: 'language', label: job.requiredLanguages.map(fixMojibake).join(', ') });
  }

  const companyLinkTitle = formatMessage(
    { id: 'hit.companyLinkTitle', defaultMessage: 'Open company profile: data and insights for {company}', description: 'Tooltip for the employer logo and name link on a job card' },
    { company: companyDisplay },
  );

  const logo = (
    <CompanyLogo
      companyName={companyKey}
      initialsSourceName={companyDisplay || companyKey}
      registryCompanyFormDescription={job.registryCompanyFormDescription}
      isTyomarkkinatori={job.isTyomarkkinatori}
      hostedLogoUrl={job.hostedLogoUrl}
      size={96}
      loading="lazy"
    />
  );

  return (
    <article className="hit-card-v2" title={title}>
      <div className="hit-card-v2__panel" style={{ background: adjustedBrand.panel, color: adjustedBrand.on }}>
        {job.companyProfileUrl ? (
          <a href={job.companyProfileUrl} className="hit-card-v2__logo-tile" title={companyLinkTitle}>
            {logo}
          </a>
        ) : (
          <div className="hit-card-v2__logo-tile">{logo}</div>
        )}
        {city ? (
          <div className="hit-card-v2__panel-location">
            <MapPin weight="fill" className="hit-card-v2__panel-location-icon" />
            <span className="hit-card-v2__panel-location-text">{fixMojibake(city)}</span>
          </div>
        ) : null}
      </div>

      <div className="hit-card-v2__content">
        <div className="hit-card-v2__top-row">
          <div className="hit-card-v2__company-block">
            {job.companyProfileUrl ? (
              <a href={job.companyProfileUrl} className="hit-card-v2__company-name" title={companyLinkTitle}>
                <span className="hit-card-v2__company-name-text">
                  <CompanyName name={companyDisplay} />
                </span>
                <Buildings weight="fill" className="hit-card-v2__company-name-icon" />
              </a>
            ) : (
              <span className="hit-card-v2__company-name">
                <span className="hit-card-v2__company-name-text">
                  <CompanyName name={companyDisplay} />
                </span>
              </span>
            )}
          </div>
        </div>

        <div className="hit-card-v2__title-block">
          <a
            href={job.url}
            onClick={onNavigate}
            className="hit-card-v2__title"
          >
            {title}
          </a>
        </div>

        <div className="hit-card-v2__salary-row">
          {salary ? (
            <span className="hit-card-v2__salary-chip">
              <span className="hit-card-v2__salary-chip-amt">{salary}</span>
            </span>
          ) : (
            <span className="hit-card-v2__salary-not-specified">
              <Msg id="hit.salaryNotSpecified.card" defaultMessage="Salary not specified" description="Shown when salary is not specified in job card" />
            </span>
          )}
        </div>

        {metaFields.length > 0 ? (
          <div className="hit-card-v2__meta">
            {metaFields.map((field, i) => (
              <React.Fragment key={field.key}>
                {i > 0 ? <span aria-hidden className="hit-card-v2__meta-sep">·</span> : null}
                <span className="hit-card-v2__meta-item">{field.label}</span>
              </React.Fragment>
            ))}
          </div>
        ) : null}

        <a href={job.url} onClick={onNavigate} className="hit-card-v2__cta">
          <Msg id="hitV2.viewJob" defaultMessage="View job" description="Primary CTA button on the redesigned job card" />
        </a>
      </div>
    </article>
  );
};

export const SimilarJobsSection: React.FC<Props> = ({ similarJobs, seeAllHref, brandColor }) => {
  const intl = useIntl();
  const formatMessage = useFormatMessage();
  const isDark = useDocumentDarkClass();
  // brandColor is the brand panel colour (a background colour, near-black for every
  // neutral-fallback brand) and disappears as icon/text colour on the dark card; swap to the
  // dark-surface-safe accent there, matching EditorialHeadlineFacts.tsx/EditorialCompanyExtras.tsx.
  const accentColor = brandColor ? (isDark ? brandAccentOnDark(brandColor) : brandColor) : undefined;
  if (!similarJobs || similarJobs.length === 0) return null;

  return (
    <section className="mt-6" aria-labelledby="similar-jobs-heading">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <h2
            id="similar-jobs-heading"
            className="text-lg font-semibold text-jc-ink tracking-tight"
          >
            <Msg
              id="jobDetail.similarJobs.algoliaListTitle"
              defaultMessage="Similar jobs"
              description="Heading for the list of similar job listings from search"
            />
          </h2>
          <div
            className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border ${accentColor ? '' : 'bg-jc-accent-muted border-jc-accent/20 dark:border-jc-accent/35 dark:bg-[#1A2E22]'}`}
            style={accentColor ? { background: `${accentColor}26`, borderColor: `${accentColor}40` } : undefined}
            aria-hidden
          >
            <Briefcase className={`w-4 h-4 ${accentColor ? '' : 'text-jc-cta dark:text-jc-accent'}`} style={accentColor ? { color: accentColor } : undefined} strokeWidth={2} />
          </div>
        </div>
        <div className="flex items-center gap-2">
          {seeAllHref ? (
            <a
              href={seeAllHref}
              className={`inline-flex items-center rounded-md border px-2 py-1 text-[11px] font-medium transition-colors ${accentColor ? 'hover:brightness-95' : 'border-jc-accent/30 bg-jc-accent-muted/45 text-jc-cta hover:bg-jc-accent-muted/70 dark:border-jc-accent/45 dark:bg-[#1A2E22]/90 dark:text-jc-accent dark:hover:bg-[#1A2E22]'}`}
              style={accentColor ? { borderColor: `${accentColor}4D`, background: `${accentColor}1F`, color: accentColor } : undefined}
            >
              <Msg
                id="jobDetail.similarJobs.seeAll"
                defaultMessage="See all"
                description="CTA in similar jobs header to open full search results"
              />
            </a>
          ) : null}
        </div>
      </div>
      <p className="mb-3 text-[11px] text-jc-muted leading-snug">
          <Msg
            id="jobDetail.similarJobs.sourceNote"
            defaultMessage="From JobCrawls search: same role title and primary location as this listing (this job excluded). Up to 8 results."
            description="Explains that similar jobs are from Algolia using role and location filters"
          />
      </p>

      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {similarJobs.map((job) => {
          const title = job.normalisedTitle || job.title || '';
          return (
            <li key={job.objectID} style={{ containerType: 'inline-size' }}>
              <SimilarJobCard
                job={job}
                onNavigate={() =>
                  trackJobClick({
                    jobId: job.objectID,
                    jobTitle: title,
                    companyId: job.companyId || '',
                    source: 'similar_jobs',
                    locale: intl.locale,
                  })
                }
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
};
